import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Calendar,
  Clock,
  Accessibility,
  SmilePlus,
  Map,
  ArrowLeft,
} from "lucide-react";
import { SEOHead, TopNavbar, Footer, LoginModal } from "@components/organisms";
import { firebaseSignInAnonymously } from "@services/config/firebase";
import { LocalitySelection } from "@components/organisms/LocalitySelection";
import { CheckoutModal } from "@components/organisms/CheckoutModal";
import { SeatMapModal } from "@components/organisms/SeatMapModal";
import { QRTicketViewer } from "@components/organisms/QRTicketViewer";
import {
  SuccessModal,
  ErrorModal,
  LoadingPage,
  PurchaseBar,
  PurchaseSummary,
} from "@components/molecules";
import { Button, Text } from "@components/atoms";
import { useEvents } from "@modules/events/hooks";
import { useEventLocalities } from "@modules/events/hooks/useEventLocalities";
import { InfoRow } from "@modules/events/components";
import { resolvePurchaseEntry } from "@modules/events/utils/purchaseEntry";
import { formatDate, formatDateRange, formatCurrency, toTitleCase } from "@shared/utils";
import { ROUTES } from "@shared/constants";
import { useAuth } from "@app/providers/useAuth";

/**
 * Descuento Banco de Guayaquil — solo para CHILL&CHELA FESTIVALS (id 1860).
 * Muestra el precio sin "Desde" y un bloque de promo con logos (banco + Amex)
 * en la descripción. Fijo por pedido puntual; si a futuro se necesita
 * configurable por evento, ver el pendiente de "nota de pago por evento".
 */
const CHILLYCHELA_EVENT_ID = 1860;

/**
 * Paso del flujo de compra que exige sesión (seleccion-localidades-inline RF-9).
 * `localityId: null` = mapa completo, en eventos 100 % numerados.
 */
type PendingAction =
  | { type: "checkout" }
  | { type: "seatmap"; localityId: number | null };

/** Build a Google Maps query: address + city (dynamic) + Ecuador. */
function buildMapQuery(address: string, city: string): string | null {
  const addr = address.trim();
  if (!addr) return null;
  const cityPart = city && city.trim() ? `${city.trim()}, Ecuador` : "Ecuador";
  return `${addr}, ${cityPart}`;
}

/**
 * Event detail page.
 * Gradient background (neutral-60 → primary-110).
 * Two-column layout: left = event info, right (410 px) = image + map.
 * Manages full purchase flow: ticket selection → checkout → success → QR.
 */
export function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: events } = useEvents();
  const { user } = useAuth();
  const eventKey = useMemo(() => decodeURIComponent(slug ?? ""), [slug]);

  const event = events?.find((e) => e.slug === eventKey || e.id === eventKey);

  // Localities — preferir el id numérico del evento (ya resuelto por useEvents) para
  // no depender del slug (que puede diferir entre la lista ALB y la API mobile).
  const { data: localityData } = useEventLocalities(
    event?.id != null ? String(event.id) : (event?.slug ?? eventKey),
  );
  const localities = useMemo(
    () => localityData?.localities ?? [],
    [localityData],
  );
  const enrichedEvent = event && localityData?.event
    ? {
        ...event,
        ...localityData.event,
        dateend: localityData.event.dateend ?? event.dateend,
        // bug-20260930-seating-type-detalle: seating_type solo viene en el listado.
        seatingType: localityData.event.seatingType ?? event.seatingType,
        payment_method_deuna:
          localityData.event.payment_method_deuna ?? event.payment_method_deuna ?? null,
        payment_method_jepfast:
          localityData.event.payment_method_jepfast ?? event.payment_method_jepfast ?? null,
        payment_method_payphone:
          localityData.event.payment_method_payphone ?? event.payment_method_payphone ?? null,
        payment_method_cooperco:
          localityData.event.payment_method_cooperco ?? event.payment_method_cooperco ?? null,
        payment_method_plux:
          localityData.event.payment_method_plux ?? event.payment_method_plux ?? null,
      }
    : localityData?.event ?? event;

  // Flow state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSeatMap, setShowSeatMap] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showTimeout, setShowTimeout] = useState(false);
  const [seatMapLocalityId, setSeatMapLocalityId] = useState<number | null>(null);
  const [selections, setSelections] = useState<Record<string, number>>({});
  /** bug-20260421: selected seats for NUMBERED events. */
  const [selectedSeats, setSelectedSeats] = useState<Array<{ seatId: number; seatLabel: string; reservedUntil: string }>>([]);
  const [seatSessionToken, setSeatSessionToken] = useState<string | null>(null);
  const [ticketCode] = useState(() => `TKT-${Date.now()}`);
  const [checkoutKey, setCheckoutKey] = useState(0);
  /** Acción que quedó a medias por falta de sesión; se retoma al autenticarse (RF-9).
   *  Va en un ref y no en estado: no se pinta, solo espera a que AuthProvider propague. */
  const pendingAction = useRef<PendingAction | null>(null);
  const navigate = useNavigate();

  const totalTickets = useMemo(
    () => Object.values(selections).reduce((s, q) => s + q, 0),
    [selections],
  );

  /** Subtotal de entradas: la comisión y el descuento por método se calculan en el checkout. */
  const subtotal = useMemo(
    () =>
      localities.reduce(
        (sum, loc) => sum + loc.price * (selections[loc.id] ?? 0),
        0,
      ),
    [localities, selections],
  );

  const runAction = useCallback((action: PendingAction) => {
    if (action.type === "checkout") {
      setCheckoutKey((k) => k + 1);
      setShowCheckout(true);
      return;
    }
    setSeatMapLocalityId(action.localityId);
    setShowSeatMap(true);
  }, []);

  // Al autenticarse, retomar lo que el comprador estaba haciendo.
  useEffect(() => {
    const action = pendingAction.current;
    if (!user || !action) return;
    pendingAction.current = null;
    runAction(action);
  }, [user, runAction]);

  // Track event view (analytics WIP — service not yet implemented)
  useEffect(() => { /* no-op until analytics service is ready */ }, [event?.id]);

  if (!event && !events) {
    return <LoadingPage withGradient />;
  }

  if (!event && events) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-neutral-60 to-primary-110">
        <Text variant="h4" className="text-white">
          Evento no encontrado
        </Text>
      </div>
    );
  }

  const currentEvent = enrichedEvent!;

  // La sesión anónima se crea en la PRIMERA interacción con la lista, no al pagar:
  // así AuthProvider ya cacheó el token de Firebase cuando el checkout llama a save_sale.
  const handleLocalityChange = (localityId: string, qty: number) => {
    if (!user) void firebaseSignInAnonymously().catch(() => {});
    setSelections((prev) => ({ ...prev, [localityId]: qty }));
  };

  const handleSeatMapContinue = (params: {
    selections: Record<string, number>;
    selectedSeats: Array<{ seatId: number; seatLabel: string; reservedUntil: string }>;
    sessionToken: string;
  }) => {
    setSelectedSeats(params.selectedSeats);
    setSeatSessionToken(params.sessionToken);
    setSelections(params.selections);
    setShowSeatMap(false);
    setCheckoutKey((k) => k + 1);
    setShowCheckout(true);
  };

  const isNumberedEvent = currentEvent?.seatingType === 'NUMBERED';
  // localidades-mixtas: eventos NUMBERED con al menos una localidad general
  // (withSeats !== 1) entran por selección de cantidad y eligen asiento por localidad.
  const hasGeneralLocality = localities.some((l) => l.withSeats !== 1);

  // resolvePurchaseEntry (decisión pura, testeada en purchaseEntry.test.ts):
  // - 100% numerado: el mapa de asientos con todas las localidades sigue siendo la entrada.
  // - general o mixto: la lista inline ya está en la página; no hay nada que abrir.
  const showsInlineSelection =
    resolvePurchaseEntry(isNumberedEvent, hasGeneralLocality) !== "seatmap-all";

  // Corre la acción exigiendo sesión. Si no hay, crea una anónima de Firebase de
  // forma transparente; si eso falla, cae al login y la acción se retoma después.
  const withSession = async (action: PendingAction) => {
    if (!user) {
      try {
        await firebaseSignInAnonymously();
        // AuthProvider detectará el cambio vía onAuthStateChanged
      } catch {
        // Si falla el anónimo (sin conexión, etc.), mostrar login como fallback
        pendingAction.current = action;
        setShowLoginModal(true);
        return;
      }
    }
    runAction(action);
  };

  // RF-8: solo se renderiza en eventos 100% numerados, donde no hay lista inline
  // y este botón es la única entrada al mapa de asientos completo.
  const handleBuyClick = () => {
    void withSession({ type: "seatmap", localityId: null });
  };

  // Si ya hay sesión, retoma de una; si no, el efecto de arriba la retoma en
  // cuanto AuthProvider propague el usuario.
  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    const action = pendingAction.current;
    if (user && action) {
      pendingAction.current = null;
      runAction(action);
    }
  };

  const handleCheckoutSuccess = () => {
    setShowCheckout(false);
    setShowSuccess(true);
  };

  const handleCheckoutTimeout = () => {
    setShowCheckout(false);
    setShowTimeout(true);
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    setShowQR(true);
  };

  return (
    <>
      <SEOHead
        title={currentEvent.name}
        description={
          currentEvent.summary ||
          `Compra entradas para ${currentEvent.name} en TicketIn. Evento en ${currentEvent.location.venue || currentEvent.location.city}.`
        }
        canonical={`https://ticketin.ec/events/${currentEvent.slug || currentEvent.id}`}
        ogImage={currentEvent.image ?? undefined}
        ogType="event"
        eventSchema={{
          name: currentEvent.name,
          description: currentEvent.summary ?? currentEvent.description,
          startDate: currentEvent.dateevent,
          location: currentEvent.location.venue || currentEvent.location.city || "Ecuador",
          image: currentEvent.image,
          price: currentEvent.price,
          url: `https://ticketin.ec/events/${currentEvent.slug || currentEvent.id}`,
        }}
      />
      {/* pb extra donde va la barra fija (por debajo de xl): si no, taparía el final de la página (RF-7). */}
      <div
        className={`flex min-h-screen flex-col bg-gradient-to-b from-neutral-60 to-primary-110 ${totalTickets > 0 ? "pb-24 min-[1400px]:pb-0" : ""}`}
      >
        <TopNavbar events={events ?? []} opaque />

        <main className="flex flex-1 flex-col lg:flex-row gap-8 lg:gap-16 px-4 sm:px-8 lg:px-20 py-8 lg:py-12">
          {/* ====== LEFT COLUMN ====== */}
          <div className="flex flex-1 flex-col gap-4">
            {/* Volver — mismo patrón que MyTicketsPage. navigate(-1) y no ROUTES.HOME
                porque acá se puede llegar desde la portada, una búsqueda o un filtro,
                y hay que devolver al comprador a donde estaba. */}
            <button
              className="flex w-fit cursor-pointer items-center gap-2 border-none bg-transparent text-white"
              onClick={() => navigate(-1)}
              aria-label="Volver"
            >
              <ArrowLeft size={26} />
            </button>

            {/* Venue */}
            {currentEvent.location.venue && (
              <div className="flex items-center gap-2">
                <MapPin size={28} className="text-neutral-30" />
                <span className="text-neutral-30" style={{ fontSize: 18 }}>
                  {currentEvent.location.venue}
                </span>
              </div>
            )}

            {/* Event name — responsive: 36px movil → 48px sm → 60px lg.
                break-words evita que un titulo largo desborde el viewport en movil. */}
            <h1 className="font-bold text-white leading-tight break-words text-4xl sm:text-5xl lg:text-[60px]">
              {currentEvent.name}
            </h1>

            {/* Summary — 14 px white */}
            {currentEvent.summary && (
              <p
                className="leading-relaxed text-white"
                style={{ fontSize: 14 }}
              >
                {currentEvent.summary}
              </p>
            )}

            {/* "Desde $X" y "Comprar entradas" se quitaron: con la lista de localidades
                en la misma página, el precio ya está a la vista y el botón no llevaba
                a ningún lado. Sobreviven SOLO en eventos 100% numerados, donde no hay
                lista inline y este botón es la única entrada al mapa de asientos. */}
            {!showsInlineSelection && (
              <div className="flex items-center gap-6">
                <span
                  className="font-semibold text-secondary-120"
                  style={{ fontSize: 14 }}
                >
                  {Number(currentEvent.id) === CHILLYCHELA_EVENT_ID
                    ? formatCurrency(currentEvent.price)
                    : `Desde ${formatCurrency(currentEvent.price)}`}
                </span>
                <Button
                  className="h-[40px] px-10 bg-secondary-120 text-primary-110 hover:bg-secondary-120 hover:opacity-90"
                  onClick={handleBuyClick}
                >
                  Comprar entradas
                </Button>
              </div>
            )}

            {/* Bloque descuento Banco de Guayaquil — solo CHILL&CHELA FESTIVALS (id 1860) */}
            {Number(currentEvent.id) === CHILLYCHELA_EVENT_ID && (
              <div
                className="flex items-center gap-3 rounded-lg px-4 py-3"
                style={{ border: "1px solid #f4a9cb", backgroundColor: "#fdeef5" }}
              >
                <span
                  className="flex-1 font-semibold italic"
                  style={{ fontSize: 13, color: "#b3004f" }}
                >
                  Descuento por pago con tarjetas Banco Guayaquil
                </span>
                <img
                  src="/images/banco-guayaquil.png"
                  alt="Banco de Guayaquil"
                  className="h-8 w-auto flex-shrink-0 object-contain"
                />
                <img
                  src="/images/american-express.png"
                  alt="American Express"
                  className="h-8 w-auto flex-shrink-0 object-contain"
                />
              </div>
            )}

            {/* Info grid — Fecha | Hora / Lugar | Edad / Accesibilidad.
                Va ARRIBA de las localidades: el comprador decide con estos datos. */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 pb-8 border-b border-white/30 min-[1400px]:grid-cols-3">
              <InfoRow
                icon={<Calendar size={20} />}
                label="Fecha"
                value={formatDateRange(currentEvent.dateevent, currentEvent.dateend)}
              />

              <InfoRow
                icon={<Clock size={20} />}
                label="Hora"
                value={currentEvent.hour ?? "Por confirmar"}
              />

              <InfoRow
                icon={<MapPin size={20} />}
                label="Lugar"
                value={toTitleCase(
                  currentEvent.location.venue ||
                    currentEvent.location.addressLine ||
                    currentEvent.address ||
                    "Por confirmar",
                )}
              />

              {currentEvent.location.city && (
                <InfoRow
                  icon={<MapPin size={20} />}
                  label="Ciudad"
                  value={toTitleCase(currentEvent.location.city)}
                />
              )}

              <InfoRow
                icon={<SmilePlus size={20} />}
                label="Edad"
                value={toTitleCase(currentEvent.ageRestriction ?? "Todo público")}
              />

              <InfoRow
                icon={<Accessibility size={20} />}
                label="Accesibilidad"
                value={toTitleCase(currentEvent.accessibility ?? "No especificado")}
              />
            </div>

            {/* Selección de localidades inline (seleccion-localidades-inline RF-1).
                En eventos 100% numerados la entrada sigue siendo el mapa completo. */}
            {showsInlineSelection && (
              <LocalitySelection
                localities={localities}
                maxPerPurchase={currentEvent.maxventadigital}
                selections={selections}
                onChange={handleLocalityChange}
                onPickSeats={(localityId) =>
                  void withSession({ type: "seatmap", localityId })
                }
                aside={
                  <PurchaseSummary
                    localities={localities}
                    selections={selections}
                    eventId={currentEvent.id}
                    onPay={() => void withSession({ type: "checkout" })}
                  />
                }
              />
            )}

            {/* Acerca del evento — va DEBAJO de las localidades: es texto de lectura,
                no de decisión, y arriba empujaba la compra fuera de la pantalla. */}
            <div className="flex flex-col gap-2">
              <span
                className="font-semibold text-white"
                style={{ fontSize: 14 }}
              >
                Acerca del evento
              </span>
              {currentEvent.description && (
                <p
                  className="leading-relaxed text-white whitespace-pre-line"
                  style={{ fontSize: 14 }}
                >
                  {currentEvent.description}
                </p>
              )}
            </div>
          </div>

          {/* ====== RIGHT COLUMN ====== */}
          <div className="flex flex-col gap-5 w-full lg:w-[410px] lg:flex-shrink-0">
            {/* Resumen tipo factura (RF-7). Solo escritorio: en móvil esta columna se
                apila al final de la página, así que ahí manda la barra fija. Sticky
                para que no se pierda al bajar por la descripción y las localidades. */}
            {/* Event image 410×410 r-16 */}
            <div
              className="overflow-hidden rounded-2xl w-full"
              style={{ aspectRatio: "1/1" }}
            >
              {currentEvent.image ? (
                <img
                  src={currentEvent.image}
                  alt={currentEvent.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-neutral-50">
                  <Text variant="body" className="text-neutral-30">
                    Sin imagen
                  </Text>
                </div>
              )}
            </div>

            {/* Mapa de localidades del recinto (RF-06.5). Antes vivía dentro del
                TicketSelectionModal; con la selección inline pasa a la página (RF-10). */}
            {currentEvent.canchaImageUrl && (
              <div className="flex flex-col gap-2">
                <span className="text-white" style={{ fontSize: 14 }}>
                  Localidades
                </span>
                <img
                  src={currentEvent.canchaImageUrl}
                  alt="Mapa de localidades del recinto"
                  className="w-full rounded-xl object-cover"
                  style={{ aspectRatio: "410/315" }}
                />
              </div>
            )}

            {/* Location label */}
            <div className="flex items-center gap-2">
              <MapPin size={24} className="text-secondary-120" />
              <span className="text-white" style={{ fontSize: 14 }}>
                Ubicación del evento
              </span>
            </div>

            {/* Interactive map — Google Maps embed via iframe, no API key required */}
            {(() => {
              const rawAddr = currentEvent.address ?? currentEvent.location.addressLine;
              const mapQuery = buildMapQuery(rawAddr ?? "", currentEvent.location.city ?? "");
              return mapQuery ? (
                <>
                  <iframe
                    title="Ubicación del evento"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed&z=15`}
                    className="rounded-xl w-full border-0"
                    style={{ aspectRatio: "410/315" }}
                    loading="lazy"
                    allowFullScreen
                  />
                  {/* "Ver mapa" button — opens full Google Maps in new tab */}
                  <div className="flex justify-end">
                    <button
                      className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-secondary-120 bg-transparent px-6 py-2 text-secondary-120 transition-colors hover:bg-secondary-120/10"
                      style={{ fontSize: 14 }}
                      onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(mapQuery)}`, "_blank")}
                    >
                      <Map size={16} />
                      Ver mapa
                    </button>
                  </div>
                </>
              ) : (
                <div
                  className="rounded-xl w-full flex items-center justify-center bg-white/10"
                  style={{ aspectRatio: "410/315" }}
                >
                  <div className="flex flex-col items-center gap-2 text-white/50">
                    <MapPin size={32} />
                    <span style={{ fontSize: 14 }}>Dirección no disponible</span>
                  </div>
                </div>
              );
            })()}
          </div>
        </main>
        <Footer />
        {/* ---- LOGIN MODAL (for unauthenticated users) ---- */}
        <LoginModal isOpen={showLoginModal} onClose={handleLoginModalClose} />

        {/* Barra fija de pago (RF-7) — solo con entradas ya elegidas. */}
        <PurchaseBar
          totalTickets={totalTickets}
          subtotal={subtotal}
          onPay={() => void withSession({ type: "checkout" })}
        />

        {/* ---- PURCHASE FLOW MODALS ---- */}
        {/* Numbered-seat map modal (katia-20260401-asientos-numerados).
            En eventos mixtos se abre acotado a una localidad (localidades-mixtas). */}
        {isNumberedEvent && (
          <SeatMapModal
            isOpen={showSeatMap}
            onClose={() => { setShowSeatMap(false); setSeatMapLocalityId(null); }}
            eventId={Number(currentEvent.id)}
            localities={localities}
            localityId={seatMapLocalityId ?? undefined}
            onContinue={handleSeatMapContinue}
          />
        )}
        <CheckoutModal
          key={`checkout-${checkoutKey}`}
          isOpen={showCheckout}
          onClose={() => setShowCheckout(false)}
          event={currentEvent}
          localities={localities}
          selections={selections}
          onSuccess={handleCheckoutSuccess}
          onTimeout={handleCheckoutTimeout}
          selectedSeats={selectedSeats.length > 0 ? selectedSeats : undefined}
          seatSessionToken={seatSessionToken ?? undefined}
        />
        <SuccessModal
          isOpen={showSuccess}
          onClose={handleSuccessClose}
          title="¡Compra realizada con éxito!"
          message='Tus tickets ya están listos. Puedes verlos ahora o acceder a ellos más tarde desde "Mis tickets".'
          buttonText="Ver mis tickets"
          secondaryButtonText="Ir a inicio"
          onSecondaryClick={() => {
            setShowSuccess(false);
            navigate(ROUTES.HOME);
          }}
        />
        <ErrorModal
          isOpen={showTimeout}
          onClose={() => setShowTimeout(false)}
          title="Tiempo agotado"
          message="El tiempo para completar tu compra ha expirado. Por favor, intenta nuevamente."
        />
        <QRTicketViewer
          isOpen={showQR}
          onClose={() => setShowQR(false)}
          eventName={currentEvent.name}
          eventImage={currentEvent.image}
          localityName="GENERAL"
          ticketCount={totalTickets}
          eventDate={formatDate(currentEvent.dateevent)}
          ticketCode={ticketCode}
          qrSrc="/images/qr.png"
        />
      </div>
    </>
  );
}
